import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ConsentCategories,
  DEFAULT_CONSENT,
  loadConsent,
  saveConsent,
} from "./consent";

type ConsentContextValue = {
  consent: ConsentCategories;
  setConsent: (c: ConsentCategories) => void;
  isDecided: boolean;
  preferencesOpen: boolean;
  openPreferences: () => void;
  closePreferences: () => void;
};

const ConsentContext = createContext<ConsentContextValue | null>(null);
export const useConsent = () => {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used within ConsentProvider");
  return ctx;
};

export const ConsentProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [consent, setConsentState] =
    useState<ConsentCategories>(DEFAULT_CONSENT);
  const [isDecided, setIsDecided] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  useEffect(() => {
    const c = loadConsent();
    setConsentState(c);
    setIsDecided(!!localStorage.getItem("cookie_consent_v2"));

    // GA4 Consent Mode defaults (deny until opt-in)
    (window as any).dataLayer = (window as any).dataLayer || [];
    function gtag(...args: any[]) {
      (window as any).dataLayer.push(arguments);
    }
    (window as any).gtag = gtag;
    gtag("consent", "default", {
      ad_storage: "denied",
      analytics_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      wait_for_update: 500,
    });
  }, []);

  const setConsent = (c: ConsentCategories) => {
    saveConsent(c);
    setConsentState(c);
    setIsDecided(true);

    // Update Google Consent Mode v2
    (window as any).gtag?.("consent", "update", {
      analytics_storage: c.analytics ? "granted" : "denied",
      ad_storage: c.marketing ? "granted" : "denied",
      ad_user_data: c.marketing ? "granted" : "denied",
      ad_personalization: c.marketing ? "granted" : "denied",
    });

    // Meta Pixel consent update
    if ((window as any).fbq) {
      if (c.marketing) (window as any).fbq("consent", "grant");
      else (window as any).fbq("consent", "revoke");
    }

    // after updating cookies, state, and gtag:
    window.dispatchEvent(new CustomEvent("consent:updated", { detail: c }));
  };

  const value = useMemo(
    () => ({
      consent,
      setConsent,
      isDecided,
      preferencesOpen,
      openPreferences: () => setPreferencesOpen(true),
      closePreferences: () => setPreferencesOpen(false),
    }),
    [consent, isDecided, preferencesOpen]
  );

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
};
